import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Clock3, Loader2, LogIn, LogOut, MapPin } from 'lucide-react';
import { useSharedAttendanceClock } from '../contexts/AttendanceClockContext';
import { useAttendanceLocationAccess } from '../hooks/useAttendanceLocationAccess';
import { useToast } from '../hooks/useToast';
// Face check-in. The same gate the HRMS Attendance page applies — this button
// used to be the one ungated way in, which meant anyone required to prove who
// they are could simply press here instead.
import { useFaceCheckGate } from '../contexts/FaceCheckGateContext';
import FaceCheckModal from '../hrms/components/attendance/FaceCheckModal';

type ClockConfirmation = {
  action: 'in' | 'out';
  description: string;
  left: number;
  stamp: number;
  title: string;
  top: number;
};

export default function SidebarAttendanceClock() {
  const clockState = useSharedAttendanceClock();
  return <SidebarAttendanceClockControl clockState={clockState} />;
}

function SidebarAttendanceClockControl({ clockState }: {
  clockState: ReturnType<typeof useSharedAttendanceClock>;
}) {
  const { toast } = useToast();
  const location = useAttendanceLocationAccess(
    clockState.requiresLocation && !clockState.loading,
  );
  const faceGate = useFaceCheckGate();
  const [faceModal, setFaceModal] = useState<'enroll' | 'verify' | null>(null);
  const [confirmation, setConfirmation] = useState<ClockConfirmation | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!confirmation) return undefined;

    const timeout = window.setTimeout(() => setConfirmation(null), 3600);
    return () => window.clearTimeout(timeout);
  }, [confirmation]);

  const action = clockState.nextAction;
  const isBusy = clockState.busy !== null;
  const locationBlocked =
    !clockState.loading &&
    clockState.requiresLocation && location.status !== 'granted';
  const isCheckingLocation = locationBlocked && location.status === 'checking';
  const isWaiting = clockState.loading || isBusy || isCheckingLocation;
  const isDisabled = clockState.onLeave || isWaiting || (!locationBlocked && action === null);
  const label = clockState.onLeave
    ? 'On Leave'
    : locationBlocked
    ? isCheckingLocation
      ? 'Checking location'
      : 'Allow Location'
    : clockState.loading
    ? 'Checking attendance'
    : isBusy
    ? clockState.busy === 'in'
      ? 'Clocking in'
      : 'Clocking out'
    : action === 'out'
      ? 'Clock Out'
      : action === 'in'
        ? 'Clock In'
        : 'Attendance done for today';
  const tooltipText = clockState.onLeave
    ? 'Clock In is unavailable while you are on approved leave.'
    : locationBlocked
    ? location.error || 'Allow location access to use attendance.'
    : clockState.error || label;
  const Icon = isWaiting
    ? Loader2
    : locationBlocked
      ? MapPin
      : clockState.active
        ? LogOut
        : action === 'in'
          ? LogIn
          : Clock3;
  const buttonClassName = [
    'gt-sidebar-clock-btn',
    action === 'in' && 'is-clock-in',
    action === 'out' && 'is-clock-out',
    clockState.active && 'is-active',
    clockState.doneForToday && 'is-done',
    isDisabled && 'is-disabled',
  ].filter(Boolean).join(' ');

  /** The bubble and toast a successful punch shows. Shared, because a face
   *  punch is written by hrms inside the modal and never comes back through
   *  the branch below — without this it would succeed in silence. */
  const announceClocked = (done: 'in' | 'out', description?: string) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    setConfirmation({
      action: done,
      description: description ?? (done === 'in'
        ? "You're marked present for today."
        : 'Your attendance is saved for today.'),
      left: rect ? rect.right + 12 : 12,
      stamp: Date.now(),
      title: done === 'in' ? 'Clocked in successfully' : 'Clocked out successfully',
      top: rect ? rect.top + rect.height / 2 : 24,
    });
    toast({
      title: done === 'in' ? 'Clocked in' : 'Clocked out',
      description: description ?? (done === 'in'
        ? "You're marked present for today."
        : 'Have a good rest of your day.'),
    });
  };

  const handleClick = async () => {
    if (isDisabled) return;
    if (locationBlocked) {
      await location.requestAccess();
      return;
    }
    if (!action) return;
    setConfirmation(null);

    // Face check runs on the way IN only. Clocking out is not an identity
    // claim — nobody gains anything by ending someone else's day, and a camera
    // that refuses to let you leave is the failure mode worth avoiding most.
    if (action === 'in' && faceGate.blocked) {
      // Entitled, but the check cannot run. Refuse rather than fall through to
      // an ordinary punch: falling through is the downgrade this gate exists
      // to close, and there is no longer an ungated button to send people to.
      toast({
        title: 'Face check unavailable',
        description:
          "We can't verify your face right now, so this punch was not recorded. "
          + 'Try again in a moment, or ask HR to mark you in.',
        variant: 'destructive',
      });
      // Usually a side-car blip. Re-probe so the next press finds it back
      // rather than making the user reload.
      void faceGate.refresh();
      return;
    }
    if (action === 'in' && faceGate.ready) {
      setFaceModal(faceGate.status?.enrolled ? 'verify' : 'enroll');
      return;
    }

    try {
      await clockState.clock(action);
      announceClocked(action);
    } catch (err: any) {
      toast({
        title: action === 'in' ? "Couldn't clock in" : "Couldn't clock out",
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="gt-sidebar-clock-slot">
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        onClick={handleClick}
        aria-disabled={isDisabled}
        aria-describedby="gt-sidebar-clock-tooltip"
        aria-label={label}
        aria-pressed={clockState.active}
        title={tooltipText}
      >
        <Icon className={`gt-sidebar-clock-btn__icon${isWaiting ? ' is-spinning' : ''}`} />
        {clockState.active && <span className="gt-sidebar-clock-btn__dot" aria-hidden />}
      </button>
      <span id="gt-sidebar-clock-tooltip" className="gt-sidebar-clock-tooltip" role="tooltip">
        {tooltipText}
      </span>
      {/* Mounted only once hrms has said this user may use it, so an org
          without the feature renders nothing at all and never touches the
          camera. */}
      {faceGate.ready && (
        <FaceCheckModal
          open={faceModal !== null}
          mode={faceModal ?? 'verify'}
          status={faceGate.status}
          workfromhome={clockState.workfromhome}
          onClose={() => setFaceModal(null)}
          onClockedIn={async () => {
            setFaceModal(null);
            announceClocked('in', "Face verified — you're marked present for today.");
            // The punch was written by hrms, not by clockState, so the shared
            // controller still believes it is clocked out — and presence is
            // published off that state. Without this refresh the employee is
            // clocked in while chat still shows them offline, and both this
            // button and the attendance page keep offering Clock In.
            await clockState.refresh();
          }}
          onEnrolled={async () => {
            await faceGate.refresh();
            // Enrolment does not clock anyone in — it only registers the face.
            // Reopen in verify mode so the Clock In they originally pressed
            // actually happens, instead of silently doing nothing.
            setFaceModal('verify');
          }}
        />
      )}
      {confirmation && createPortal(
        <div
          key={confirmation.stamp}
          className={`gt-sidebar-clock-confirmation is-${confirmation.action}`}
          role="status"
          aria-live="polite"
          style={{ left: confirmation.left, top: confirmation.top }}
        >
          <CheckCircle2 className="gt-sidebar-clock-confirmation__icon" aria-hidden />
          <span className="gt-sidebar-clock-confirmation__copy">
            <strong>{confirmation.title}</strong>
            <small>{confirmation.description}</small>
          </span>
        </div>,
        document.body,
      )}
    </div>
  );
}
